'use client'

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'

import Image from 'next/image'
import { Text } from '@/components'
import { CreateAccount } from './CreateAccount/CreateAccount'
import { SignIn } from './SignIn/SignIn'
import { closeStyle } from './SignInPopup.styles'
import { appSelectors, useAppStore } from '@/store'
import { Config } from '@/config/config'
import { Cross2Icon } from '@radix-ui/react-icons'
import { Else, If, Then, When } from 'react-if'
import { useRouter, useSearchParams } from 'next/navigation'

export interface SignInPopupRef {
  open: () => void
  close: () => void
}

const SignInPopup = forwardRef<SignInPopupRef, unknown>((_, ref) => {
  const [isVisible, setIsVisible] = useState<boolean>(false)
  const [isSignIn, setIsSignIn] = useState<boolean>(true)
  const { setSignupVisible, setUserVerifyEmail } = useAppStore(
    appSelectors.actions,
  )
  const { isSignUpVisible } = useAppStore(appSelectors.status)
  const router = useRouter()
  const params = useSearchParams()
  const onFinish = useCallback(() => {
    const redirectTo = params.get('redirectTo')
    if (redirectTo) {
      router.replace(redirectTo)
    } else {
      router.refresh()
    }
  }, [params, router])

  const handleClose = useCallback(() => {
    setSignupVisible(false)
    onFinish()
  }, [onFinish, setSignupVisible])

  useImperativeHandle(
    ref,
    () => ({
      open: () => {
        setIsVisible(true)
        setIsSignIn(true)
        setUserVerifyEmail()
      },
      close: handleClose,
    }),
    [handleClose, setUserVerifyEmail],
  )

  const handleFormSwap = (isSignIn: boolean) => {
    setIsSignIn(isSignIn)
  }

  useEffect(() => {
    setIsVisible(isSignUpVisible)
    return () => {
      setIsVisible(false)
    }
  }, [isSignUpVisible])

  return (
    <When condition={isVisible}>
      <div className={'SignInPopupOverlay'}>
        <div
          className={['SignInPopupOverlay__Form'].join(' ')}
          style={{
            backgroundImage: 'url(/assets/images/sign-in-up-bg.png)',
            backgroundPositionX: !isSignIn ? 0 : '',
          }}
        >
          <div
            style={{
              left: !isSignIn ? 0 : 'auto',
              right: !isSignIn ? 'auto' : 0,
            }}
            className={'SignInPopupOverlay__Form__left'}
          >
            <div className="SignInPopupOverlay__Form__left__header">
              <Image
                src="/assets/svg/logo.svg"
                alt={Config.APP_NAME}
                width={90}
                height={60}
              />
              <Text variant="heading2Xl" className="gradient-text-white">
                {Config.APP_NAME}.com
              </Text>
            </div>
          </div>
          <>
            <Cross2Icon
              className="p-6 text-white"
              style={closeStyle}
              onClick={handleClose}
            />
            <If condition={isSignIn}>
              <Then>
                <SignIn setIsSignIn={handleFormSwap} onClose={handleClose} />
              </Then>
              <Else>
                <CreateAccount
                  setIsSignIn={handleFormSwap}
                  onClose={handleClose}
                />
              </Else>
            </If>
          </>
        </div>
      </div>
    </When>
  )
})

SignInPopup.displayName = 'SignInPopup'

export { SignInPopup }
